
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { Filter } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Game } from '@/types/game';
import GameCard from '@/components/game/GameCard';
import { SEO } from '@/components/SEO';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const GAMES_PER_PAGE = 12;

const CategoryPage = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [sortBy, setSortBy] = useState<'newest' | 'popular' | 'rating'>('newest');

  const { data: category, isLoading: isCategoryLoading, error: categoryError } = useQuery({
    queryKey: ['category', categorySlug],
    queryFn: async () => {
      if (!categorySlug) throw new Error('Category slug is required');

      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('slug', categorySlug)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!categorySlug,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  const {
    data: gamesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isGamesLoading,
    error: gamesError,
  } = useInfiniteQuery({
    queryKey: ['category-games', categorySlug, sortBy],
    queryFn: async ({ pageParam = 0 }) => {
      if (!categorySlug) throw new Error('Category slug is required');

      let query = supabase
        .from('games')
        .select('*')
        .eq('category', categorySlug)
        .eq('hidden', false)
        .range(pageParam * GAMES_PER_PAGE, (pageParam + 1) * GAMES_PER_PAGE - 1);

      if (sortBy === 'newest') {
        query = query.order('created_at', { ascending: false });
      } else if (sortBy === 'popular') {
        query = query.order('play_count', { ascending: false });
      } else if (sortBy === 'rating') {
        query = query.order('rating', { ascending: false });
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((dbGame): Game => ({
        id: dbGame.id,
        title: dbGame.title,
        description: dbGame.description || '',
        thumbnail: dbGame.thumbnail || '',
        category: dbGame.category,
        featured: dbGame.featured || false,
        trending: dbGame.trending || false,
        isNew: dbGame.is_new || false,
        rating: Number(dbGame.rating) || 0,
        playCount: dbGame.play_count || 0,
        fakePlayCount: dbGame.fake_play_count || undefined,
        createdAt: dbGame.created_at!,
        updatedAt: dbGame.updated_at!,
        tags: dbGame.tags || [],
        embedType: 'iframe',
        gameUrl: dbGame.game_url || '',
      }));
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.length === GAMES_PER_PAGE ? pages.length : undefined;
    },
    enabled: !!categorySlug,
    initialPageParam: 0,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  const [isFetching] = useInfiniteScroll({
    hasNextPage: hasNextPage || false,
    fetchNextPage,
    isFetchingNextPage,
  });

  const allGames = gamesData?.pages.flat() || [];

  // Show error state if there are critical errors
  if (categoryError || gamesError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {categoryError ? 'Category not found' : 'Error loading games'}
            </h1>
            <p className="text-gray-600 mb-8">
              {categoryError 
                ? 'The category you are looking for does not exist or has been removed.'
                : 'We encountered an error loading the games. Please try again later.'
              }
            </p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Generate SEO data using category's SEO fields
  const seoTitle = category?.seo_title || `${category?.name || 'Category'} Games - Free Online ${category?.name || 'Category'} Games | TerraGamers`;
  const seoDescription = category?.seo_description || category?.description || `Play free ${category?.name || 'category'} games online. Discover the best ${category?.name || 'category'} games on TerraGamers.`;
  const seoKeywords = category?.meta_keywords || `${category?.name || 'category'} games, free online ${category?.name || 'category'} games, ${category?.name || 'category'} browser games`;

  return (
    <>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        url={`https://terragamers.com/category/${categorySlug}`}
        canonical={`https://terragamers.com/category/${categorySlug}`}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Category Header */}
        <section className="w-full py-8 md:py-16">
          <div className="w-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">
                {isCategoryLoading ? 'Loading...' : category ? `${category.name} Games` : 'Category Games'}
              </h1>
              {category && category.description && (
                <p className="text-gray-600">{category.description}</p>
              )}
            </div>
            {/* Sort By Select */}
            <div className="flex items-center gap-2 shrink-0">
              <label htmlFor="sort-by" className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <Filter className="w-4 h-4" />
                Sort by:
              </label>
              <Select 
                value={sortBy} 
                onValueChange={(value) => setSortBy(value as 'newest' | 'popular' | 'rating')}
              >
                <SelectTrigger id="sort-by" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="popular">Popular</SelectItem>
                  <SelectItem value="rating">Rating</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Games Section */}
        <section className="w-full pb-8 md:pb-16">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            {/* Games Grid */}
            {isGamesLoading ? (
              <div className="flex justify-center items-center py-12 md:py-20">
                <div className="animate-spin rounded-full h-16 w-16 md:h-32 md:w-32 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {allGames.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <h3 className="text-xl font-semibold mb-2">No games found</h3>
                    <p>This category doesn't have any games yet. Check back later!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3 md:gap-4 lg:gap-6">
                    {allGames.map((game) => (
                      <GameCard key={game.id} game={game} />
                    ))}
                  </div>
                )}

                {/* Loading indicator */}
                {isFetchingNextPage && (
                  <div className="flex justify-center items-center py-8 md:py-12">
                    <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-b-2 border-blue-600"></div>
                  </div>
                )}

                {/* End of results */}
                {!hasNextPage && allGames.length > 0 && (
                  <div className="text-center py-8 md:py-12">
                    <p className="text-gray-600 text-base md:text-lg">🎉 You've seen all the games in this category!</p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default CategoryPage;
